import { useState, useEffect, useRef, useCallback } from "react";
import BlogViewToggle from "./BlogViewToggle";

function useVideoVisibility(threshold = 0) {
  const ref = useRef(null);

  const bucketUrl = "https://storage.googleapis.com/sportynerd-bucket1";

  useEffect(() => {
    const video = ref.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold }
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return ref;
}

function VideoItem({ src, muted, contain, firstVideoRef, controls }) {
  const visibilityRef = useVideoVisibility();

  // Merge the visibility ref with the optional firstVideoRef
  const mergedRef = useCallback(
    (el) => {
      visibilityRef.current = el;
      if (firstVideoRef) firstVideoRef.current = el;
    },
    [firstVideoRef, visibilityRef]
  );

  return (
    <video
      ref={mergedRef}
      src={src}
      muted={muted}
      loop
      playsInline
      controls={controls}
      style={
        contain
          ? { objectFit: "contain", width: "100%", height: "100%", objectPosition: "center" }
          : { objectFit: "cover", width: "100%", height: "100%" }
      }
    />
  );
}

export default function SportyNerdDeck({
  title,
  tech,
  date,
  data = [],
  square = false,
  contain = false,
  captionTop = false,
  soundToggle = false,
  caption = "",
  controls = true,
}) {
  const [toggleViewState, setToggleViewState] = useState(0); // 0 = muted, 1 = sound on
  const firstVideoRef = useRef(null);

  const bucketUrl = "https://storage.googleapis.com/sportynerd-bucket1";

  // Handle sound toggle for the first video
  useEffect(() => {
    const video = firstVideoRef.current;
    if (!video) return;

    if (toggleViewState === 1) {
      video.muted = false;
      video.volume = 1.0;
      video.play().catch(() => {});
    } else {
      video.muted = true;
    }
  }, [toggleViewState]);

  const captionStyle = {
    fontSize: "var(--font-size-teeny)",
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    color: "var(--white80)",
    padding: "4px 12px",
    borderRadius: "40px",
    whiteSpace: "nowrap",
    ...(captionTop ? { top: "8px" } : { bottom: "8px" }),
  };

  function renderItem(item, index) {
    const isMuted = index !== 0 || toggleViewState !== 1;
    const ref = index === 0 ? firstVideoRef : null;
    const videoControls = typeof item.controls === 'boolean' ? item.controls : controls;

    return item.type === "img" ? (
      <img src={bucketUrl + item.src} />
    ) : (
      <VideoItem
        src={bucketUrl + item.src}
        muted={isMuted}
        contain={contain}
        firstVideoRef={ref}
        controls={videoControls}
        style = { { zIndex : 9999 } } 
      />
    );
  }

  return (
    <div>
      <div className="sporty-nerd-deck-horizontal">
        <div
          className="sporty-nerd-deck-header"
          style={{ display: "flex", alignItems: "center", left: 0, width: "100%" }}
        >
          <div style={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
            <p
              style={{
                display: "inline-block",
                boxSizing: "border-box",
                fontSize: "var(--font-size-teeny)",
                padding: "4px 12px",
                border: "1px solid var(--white40)",
                borderRadius: "40px",
                color: "var(--white80)",
              }}
            >
              {title}
            </p>
          </div>

          <p className="headerTextTech">{tech}</p>

          <p
            style={{
              flex: 1,
              textAlign: "right",
              fontSize: "var(--font-size-teeny)",
              padding: "4px 12px",
              borderRadius: "40px",
              color: "var(--white80)",
            }}
          >
            {date}
          </p>
        </div>

        {soundToggle && (
          <BlogViewToggle
            value={toggleViewState === 1}
            onViewStateChange={setToggleViewState}
            color="var(--orange)"
            color2="var(--white88)"
            fontSize={16}
            icon1="VolumeOff"
            icon2="VolumeUp"
            caption={true}
          />
        )}

        {!square ? (
          <div
            className="sporty-nerd-deck-container"
            style={{ flexDirection: data.length === 2 ? "column" : "row" }}
          >
            {data.map((item, index) => (
              <div
                className={
                  data.length === 2
                    ? "sporty-nerd-deck-item-horizontal"
                    : "sporty-nerd-deck-item"
                }
                key={index}
              >
                {renderItem(item, index)}
                <p style={captionStyle}>{item.caption}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="sporty-nerd-deck-container" style={{ flexDirection: "row" }}>
            {data.map((item, index) => (
              <div
                className="sporty-nerd-deck-item"
                key={index}
                style={{ width: "400px", height: "400px" }}
              >
                {renderItem(item, index)}
                <p style={captionStyle}>{item.caption}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    
      { caption &&
        <p className="caption" style={{ textAlign: "left", paddingBottom: "40px" }}>
          {caption}
        </p>
      }
  </div>
  );
}